/**
 * Register Pending Page
 * Registration form that creates pending users awaiting admin approval
 * Uses Supabase Auth + pending_registrations table
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from '../components/auth/AuthLayout';
import { PasswordStrengthIndicator } from '../components/auth/PasswordStrengthIndicator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { validateEmail, validatePassword } from '../utils/validation';
import {
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Building2,
  User,
  Mail,
  CheckCircle2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const RegisterPending = () => {
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { registerPending } = useAuth();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    setFormError('');

    const newErrors: Record<string, string> = {};
    if (!companyName || companyName.length < 2)
      newErrors.companyName = 'Company name must be at least 2 characters';
    if (!firstName || firstName.length < 1) newErrors.firstName = 'First name is required';
    if (!lastName || lastName.length < 1) newErrors.lastName = 'Last name is required';
    if (!email) newErrors.email = 'Email is required';
    else if (!validateEmail(email)) newErrors.email = 'Please enter a valid email address';
    if (!password) newErrors.password = 'Password is required';
    else if (!validatePassword(password))
      newErrors.password =
        'Password must be at least 8 characters with uppercase, lowercase, number, and special character';
    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (!acceptTerms) newErrors.terms = 'You must accept the terms and conditions';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const result = await registerPending({
        company_name: companyName,
        first_name: firstName,
        last_name: lastName,
        email,
        password,
      });
      if (result.success) {
        setIsSubmitted(true);
      } else {
        setFormError(result.error ?? 'Registration failed');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      setFormError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <AuthLayout showSidePanel={true} sidePanelVariant="register">
        <Card
          className="w-full border-slate-200 shadow-lg rounded-2xl"
          data-testid="registration-success-card"
        >
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2
                className="text-2xl font-semibold text-slate-800 mb-2"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Registration Submitted!
              </h2>
              <p className="text-slate-600 mb-6">
                Thank you for registering. Your application is now pending admin approval. You will
                be notified via email once your account is approved.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg mb-6">
                <p className="text-sm text-slate-600">
                  <strong>Email:</strong> {email}
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Company:</strong> {companyName}
                </p>
              </div>
              <Link to="/app/login">
                <Button variant="outline" data-testid="back-to-login-btn">
                  Back to Login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout showSidePanel={true} sidePanelVariant="register">
      <Card
        className="w-full border-slate-200 shadow-lg rounded-2xl"
        data-testid="register-pending-card"
      >
        <CardHeader className="space-y-1 pb-6">
          <CardTitle
            className="text-2xl sm:text-3xl font-semibold text-slate-800"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Request Access
          </CardTitle>
          <CardDescription className="text-base text-slate-600">
            Submit your registration for admin approval
          </CardDescription>
        </CardHeader>
        <CardContent>
          {formError && (
            <Alert variant="destructive" className="mb-6" data-testid="form-error-alert">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Registration Failed</AlertTitle>
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="companyName" className="text-sm font-medium text-slate-700">
                Company Name
              </Label>
              <div className="relative">
                <Input
                  id="companyName"
                  type="text"
                  placeholder="Acme Corporation"
                  value={companyName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCompanyName(e.target.value)
                  }
                  className="h-11 pl-10 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                  data-testid="company-name-input"
                />
                <Building2
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={20}
                />
              </div>
              {errors.companyName && (
                <p
                  className="text-sm text-red-600 flex items-center gap-1 mt-1"
                  data-testid="company-name-error"
                >
                  <AlertCircle size={16} />
                  {errors.companyName}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-sm font-medium text-slate-700">
                  First Name
                </Label>
                <div className="relative">
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFirstName(e.target.value)
                    }
                    className="h-11 pl-10 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                    data-testid="first-name-input"
                  />
                  <User
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    size={20}
                  />
                </div>
                {errors.firstName && (
                  <p
                    className="text-sm text-red-600 flex items-center gap-1 mt-1"
                    data-testid="first-name-error"
                  >
                    <AlertCircle size={16} />
                    {errors.firstName}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-sm font-medium text-slate-700">
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                  className="h-11 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                  data-testid="last-name-input"
                />
                {errors.lastName && (
                  <p
                    className="text-sm text-red-600 flex items-center gap-1 mt-1"
                    data-testid="last-name-error"
                  >
                    <AlertCircle size={16} />
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Work Email
              </Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  className="h-11 pl-10 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                  data-testid="email-input"
                />
                <Mail
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={20}
                />
              </div>
              {errors.email && (
                <p
                  className="text-sm text-red-600 flex items-center gap-1 mt-1"
                  data-testid="email-error"
                >
                  <AlertCircle size={16} />
                  {errors.email}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                  className="h-11 pr-10 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                  data-testid="password-toggle-button"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <PasswordStrengthIndicator password={password} />
              {errors.password && (
                <p
                  className="text-sm text-red-600 flex items-center gap-1 mt-1"
                  data-testid="password-error"
                >
                  <AlertCircle size={16} />
                  {errors.password}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setConfirmPassword(e.target.value)
                  }
                  className="h-11 pr-10 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                  data-testid="confirm-password-toggle-button"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p
                  className="text-sm text-red-600 flex items-center gap-1 mt-1"
                  data-testid="confirm-password-error"
                >
                  <AlertCircle size={16} />
                  {errors.confirmPassword}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms"
                  checked={acceptTerms}
                  onCheckedChange={setAcceptTerms}
                  className="border-slate-300 data-[state=checked]:bg-primary-600 data-[state=checked]:border-primary-600 mt-1"
                  data-testid="terms-checkbox"
                />
                <label
                  htmlFor="terms"
                  className="text-sm text-slate-700 cursor-pointer leading-relaxed"
                >
                  I agree to the{' '}
                  <Link
                    to="/app/terms"
                    className="text-primary-600 hover:underline"
                    target="_blank"
                  >
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link
                    to="/app/privacy"
                    className="text-primary-600 hover:underline"
                    target="_blank"
                  >
                    Privacy Policy
                  </Link>
                </label>
              </div>
              {errors.terms && (
                <p
                  className="text-sm text-red-600 flex items-center gap-1"
                  data-testid="terms-error"
                >
                  <AlertCircle size={16} />
                  {errors.terms}
                </p>
              )}
            </div>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
              data-testid="submit-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                'Request Access'
              )}
            </Button>
          </form>
          <div className="mt-6">
            <p className="text-center text-sm text-slate-600">
              Already have an account?{' '}
              <Link
                to="/app/login"
                className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default RegisterPending;
